import type { ButtonHTMLAttributes, ReactNode } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'danger';
export type ButtonSize = 'sm' | 'md';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children: ReactNode;
}

// 押下 (:active) / focus 時もコントラストを維持する (#4 修正)。
//   - primary / danger: 押下時は色付き背景 (より濃い shade) + 白文字を維持。
//     focus は ring で表現し、bg を白にしない (白背景+白文字で見えなくなる不具合を防止)。
//   - secondary: 押下時は slate-100、focus は primary-400 ring。
// focus-visible のみ ring を出す (mouse click では ring を出さず、keyboard tab 時のみ)。
const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary:
    'bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white disabled:bg-primary-200 focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-1',
  secondary:
    'bg-white hover:bg-slate-50 active:bg-slate-100 text-slate-900 border border-slate-300 disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-1',
  danger:
    'bg-danger-600 hover:bg-danger-700 active:bg-danger-700 text-white disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-danger-600 focus-visible:ring-offset-1',
};

const SIZE_CLASS: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm rounded-md',
  md: 'px-4 py-2 text-base rounded-lg min-h-touch',
};

/**
 * 共通ボタン。アクセシビリティ: md サイズは min-h-touch (48px) でタッチ標的を確保。
 * docs/12 a11y / docs/16 デザインシステム参照。
 */
export function Button({
  variant = 'primary',
  size = 'md',
  className = '',
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      {...rest}
      className={`${VARIANT_CLASS[variant]} ${SIZE_CLASS[size]} font-medium transition-colors focus:outline-none disabled:cursor-not-allowed ${className}`.trim()}
    >
      {children}
    </button>
  );
}
